'use client';

import { useState, useCallback, useEffect } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  useGetEngageStrategyConnectorQuery,
  useRunEngageStrategyMutation,
} from 'src/api/redux/strategy';
import { useLogging } from 'src/context/logging';

const POLL_INTERVAL_MS = 10_000;

interface UseEngageStrategyConnectorOptions {
  strategyId: string;
  providerId: string;
}

function getErrorStatus(
  isError: boolean,
  error: unknown
): number | null {
  if (!isError || !error) return null;
  if (typeof error === 'object' && 'status' in (error as FetchBaseQueryError)) {
    return (error as FetchBaseQueryError).status as number;
  }
  return null;
}

export function useEngageStrategyConnector({
  strategyId,
  providerId,
}: UseEngageStrategyConnectorOptions) {
  const log = useLogging();

  const [justExecuted, setJustExecuted] = useState(false);
  const [polling, setPolling] = useState(false);

  const {
    data: connectorData,
    isLoading: isConnectorLoading,
    isError: isConnectorError,
    error: connectorError,
  } = useGetEngageStrategyConnectorQuery(
    { strategyId, providerId },
    {
      skip: !strategyId || !providerId,
      pollingInterval: justExecuted || polling ? POLL_INTERVAL_MS : 0,
    }
  );

  const errorStatus = getErrorStatus(isConnectorError, connectorError);
  const isConnector404 = errorStatus === 404;

  const isConnectorExecuting =
    errorStatus === 400 ||
    connectorData?.status === 'CREATED' ||
    connectorData?.status === 'PROCESSING';

  useEffect(() => {
    setPolling(isConnectorExecuting);
    if (justExecuted && isConnectorExecuting) {
      setJustExecuted(false);
    }
  }, [justExecuted, isConnectorExecuting]);

  const [runEngageStrategy, { isLoading: isExecuting }] =
    useRunEngageStrategyMutation();

  const handleExecuteStrategy = useCallback(async () => {
    if (!strategyId || !providerId) return;
    setJustExecuted(true);
    try {
      await runEngageStrategy({
        strategyId,
        providerIds: [providerId],
      }).unwrap();
    } catch {
      setJustExecuted(false);
      log.event('executeEngageStrategyError', { strategyId, providerId });
    }
  }, [strategyId, providerId, runEngageStrategy, log]);

  const isProcessing = justExecuted || isConnectorExecuting;

  return {
    connectorData,
    isConnectorLoading,
    isConnectorError,
    isConnector404,
    isConnectorExecuting,
    isProcessing,
    isExecuting,
    handleExecuteStrategy,
  };
}
